import { Flex, Tag, Text } from "@jsq/ds/core";
import PositionName from "~/pages/Compliance/riskRating/PositionName";
import {
  cellRight,
  emptyState,
  headerCell,
  headerSubtitle,
  nameCell,
  nameText,
  row,
  table,
} from "./styles_vanilla.css";
import { PositionRowData } from "./types";

type PositionsTableProps = {
  subtitle: string;
  rows: PositionRowData[];
};

// A two-part row reuses the shared "{account} in {entity}" renderer; a single preformatted
// row (from `positionsSummary`) already reads that way, so it renders as one label.
const RowLabel = ({ data }: { data: PositionRowData }) =>
  "name" in data && data.name !== undefined ? (
    <Text asSpan variant="regular-md">
      {data.name}
    </Text>
  ) : (
    <PositionName account={data.profileName} entity={data.accountName} />
  );

const rowTitle = (data: PositionRowData): string =>
  "name" in data && data.name !== undefined
    ? data.name
    : `${data.profileName} in ${data.accountName}`;

const PositionRow = ({ data }: { data: PositionRowData }) => (
  <Flex className={row} data-testid="existing-position-row">
    <div className={nameCell}>
      <div className={nameText} title={rowTitle(data)}>
        <RowLabel data={data} />
      </div>
    </div>
    <div className={cellRight}>
      {data.programs.length > 0 ? (
        data.programs.map((program) => (
          <Tag key={program} data-testid="program-tag">
            {program}
          </Tag>
        ))
      ) : (
        <Text variant="regular-md">--</Text>
      )}
    </div>
  </Flex>
);

export const PositionsTable = ({ subtitle, rows }: PositionsTableProps) => {
  if (rows.length === 0) {
    return (
      <Flex className={table}>
        <div className={emptyState}>
          <Text variant="secondary-md">No positions are associated with this selection.</Text>
        </div>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" className={table} data-testid="existing-positions-table">
      <Flex className={row}>
        <div className={headerCell}>
          <Text variant="strong-md">Compliance profile</Text>
          <Text variant="secondary-md" className={headerSubtitle} title={subtitle}>
            {subtitle}
          </Text>
        </div>
        <div className={cellRight}>
          <Text variant="strong-md">Compliance program</Text>
        </div>
      </Flex>
      {rows.map((data) => (
        <PositionRow key={data.id} data={data} />
      ))}
    </Flex>
  );
};
